import { useStafferOrganizationsList } from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { locationQueryOptions } from "api/queries/locationDetail";
import type { Organization } from "api/resources/Organization";
import { useAtom, useAtomValue } from "jotai";
import { activeLocationIdAtom, parseUrlLocationId, swapLocationInPath } from "./atoms";

function useUrlLocationId() {
  const { pathname } = useLocation();
  return parseUrlLocationId(pathname);
}

export function useActiveLocationId(): readonly [
  number | undefined,
  (id: number, search?: Record<string, unknown>) => void,
] {
  const urlLocationId = useUrlLocationId();
  const [storedLocationId, setStoredLocationId] = useAtom(activeLocationIdAtom);
  const routerLocation = useLocation();
  const navigate = useNavigate();

  const setActiveLocationId = (id: number, search?: Record<string, unknown>) => {
    setStoredLocationId(id);
    if (urlLocationId !== undefined && urlLocationId !== id) {
      const newPath = swapLocationInPath(routerLocation.pathname, urlLocationId, id);
      void navigate({ to: newPath, search: search ?? routerLocation.search, replace: true });
    }
  };

  return [urlLocationId ?? storedLocationId, setActiveLocationId] as const;
}

export function useActiveLocation() {
  const [activeLocationId, setActiveLocationId] = useActiveLocationId();
  const activeLocation = useQuery({
    ...locationQueryOptions(activeLocationId ?? -1),
    enabled: !!activeLocationId,
  }).data;

  // oxlint-disable-next-line typescript/no-non-null-assertion -- consumers render under a location route whose detail is seeded before mount
  return [activeLocation!, setActiveLocationId] as const;
}

// `managed: true` only ever returns full `Organization` rows; the `OrganizationProDetail`
// member of the response union is exclusive to the pro `?query=open_shifts` path. Narrow the
// union to `Organization` here so consumers don't each re-cast.
export function useManagedOrganizations() {
  const { data } = useStafferOrganizationsList({ managed: true }, { query: { staleTime: Number.POSITIVE_INFINITY } });
  return data as Array<Organization> | undefined;
}

export function useActiveOrganization() {
  const [activeLocation] = useActiveLocation();

  const organizations = useManagedOrganizations();
  return organizations?.find((org) => org.id === activeLocation?.organization_id) ?? organizations?.[0];
}

export function useActiveMarketId() {
  return useActiveLocation()[0]?.market_id;
}

export function useStoredLocationId() {
  return useAtomValue(activeLocationIdAtom);
}

export function useStoredLocation() {
  const storedLocationId = useStoredLocationId();
  return useQuery({
    ...locationQueryOptions(storedLocationId ?? -1),
    enabled: !!storedLocationId,
  }).data;
}
